/**
 * Cloudflare Turnstile widget — privacy-preserving CAPTCHA.
 *
 * Loads the Cloudflare script tag once (idempotent across mounts), renders
 * the managed widget, and surfaces the token via onVerify. If
 * VITE_TURNSTILE_SITE_KEY is unset the component renders nothing — the
 * backend mirrors this by skipping verification when its secret is unset,
 * which keeps dev/preview environments running without Turnstile config.
 *
 * Token lifecycle notes (load-bearing for the parent form):
 *  - Cloudflare tokens are SINGLE USE server-side. After a successful POST,
 *    the parent should treat any subsequent submit as needing a fresh token
 *    (call resetToken() via onExpire or clear local state on submit error).
 *  - Tokens expire after ~5 minutes if unused — the widget fires onExpire
 *    in that case so we can clear the parent's token state.
 */

import { useEffect, useRef } from 'react';

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';

// Cloudflare attaches the widget API to window.turnstile after the script
// loads. Minimal subset of the surface we actually use.
type TurnstileRenderOptions = {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback'?: (errorCode: string) => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  appearance?: 'always' | 'execute' | 'interaction-only';
  cdata?: string;
};

type TurnstileAPI = {
  render: (container: HTMLElement, opts: TurnstileRenderOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

// Track in-flight script load so concurrent mounts don't append duplicate tags.
let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile cannot load in SSR'));
  }
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      // Another instance started the load — listen on it instead of duplicating.
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed')));
      return;
    }
    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Reset so a retry can re-attempt. Otherwise every subsequent mount
      // sees a permanently-rejected promise.
      scriptPromise = null;
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export type TurnstileWidgetProps = {
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark' | 'auto';
};

export function TurnstileWidget({
  onVerify,
  onError,
  onExpire,
  theme = 'auto',
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Latest-callback refs so we can re-render the widget once without
  // re-binding stale handlers when parent re-renders.
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);
  onVerifyRef.current = onVerify;
  onErrorRef.current = onError;
  onExpireRef.current = onExpire;

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    // Dev mode: no site key configured — render nothing. The backend will
    // also skip verification when its secret is unset.
    if (!siteKey) return;

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled) return;
        if (!containerRef.current || !window.turnstile) return;
        // Render only once per mount. Strict-mode double-effect would
        // otherwise create two widgets.
        if (widgetIdRef.current) return;

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => onVerifyRef.current(token),
          'error-callback': (code) => onErrorRef.current?.(code),
          'expired-callback': () => onExpireRef.current?.(),
          'timeout-callback': () => onExpireRef.current?.(),
        });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        onErrorRef.current?.(err.message);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // No-op: widget may have already been torn down by the script.
        }
        widgetIdRef.current = null;
      }
    };
    // siteKey is a build-time constant (import.meta.env) — biome flags it as
    // unnecessary; we depend on `theme` so it stays in the list.
  }, [theme]);

  if (!siteKey) return null;

  return <div ref={containerRef} className="cf-turnstile" data-testid="turnstile-widget" />;
}

/** True when Turnstile is wired (site key present in the bundle). */
export function isTurnstileEnabled(): boolean {
  return Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);
}
